import React, { useEffect, useMemo } from 'react'
import { makeStyles } from '@mui/styles'

import { useSignatureContext } from '@/modules/signature/_helpers/SignatureContext'
import SignaturePenIcon from '@assets/images/icons/signature_pen.svg'
import CalendarIcon from '@assets/images/icons/calendar.svg'
import TextIcon from '@assets/images/icons/v2/text-indicator.svg'
import lang from '@/lang'
import { setActiveSigner, SIGNER_COLOR_CODES } from '@/redux/ducks/signature/edit'
import { dispatch } from '@/redux/ducks/lists'
import ThemeInterface from '@/theme-interface'
import { MultiSelectTypes } from '@/components/multi-select/constants'
import { Signature } from '@/modules/signature/types'
import { SIGNATURE_FIELD_TYPES } from '@/modules/base/util/constants'
import { useAppSelector } from '@/redux/store'
import { getContrastText } from '@/modules/base/helpers/getContrastText'
import SignaturePlacementItem from '@/modules/signature/components/wizard/signature-placement/signature-placement-item'
import SignaturePlacementAdd from '@/modules/signature/components/wizard/signature-placement/signature-placement-add'

type StylePropTypes = { signerColor: string, contrastColor: string }
const useStyles = makeStyles((theme: ThemeInterface) => ({
  title: {
    display: 'flex',
    fontSize: theme.fontSizes.smedium,
    paddingBottom: 20,
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
  },
  step: {
    fontSize: theme.fontSizes.smaller,
    marginBottom: 10,
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  stepTitle: {
    fontWeight: theme.fontWeights.medium,
  },
  signerInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    lineHeight: '1rem',
  },
  colorPreview: ({ signerColor }: StylePropTypes) => ({
    backgroundColor: signerColor,
    borderRadius: '50%',
    height: 12,
    width: 12,
    marginRight: 10,
  }),
  signerCount: {
    justifySelf: 'flex-end',
  },
  addArea: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 16,
    gap: 15,
  },
  signatureItems: {
    display: 'flex',
    flexDirection: 'column',
  },
  divider: {
    borderBottom: `1px solid ${theme.borderColors.default}`,
    margin: '20px 0px',
  },
  tooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    fontFamily: theme.fontFamily,
  },
  tooltipTitle: {
    fontWeight: theme.fontWeights.semiBold,
    fontSize: theme.fontSizes.default,
    marginBottom: 5,
  },
  tooltipDescription: {
    fontWeight: theme.fontWeights.regular,
    fontSize: theme.fontSizes.smaller,
    lineHeight: '1rem',
  },
}), { name: 'SignaturePlacement' })

const steps = [
  { name: 'firstStep', label: lang.signature.placement.steps.firstStep.label, description: lang.signature.placement.steps.firstStep.description },
  { name: 'secondStep', label: lang.signature.placement.steps.secondStep.label, description: lang.signature.placement.steps.secondStep.description },
  { name: 'optional', label: lang.signature.placement.steps.optional.label, description: lang.signature.placement.steps.optional.description },
]

const SignaturePlacement = () => {
  const { activeSigner, signers } = useAppSelector(state => state.signature.edit)
  const {
    placingMode,
    addItem,
    setCurrentItemIndexWithScroll,
    removeItem,
    setPlacingMode,
    items,
    currentItemIndex,
    pager: { currentPage, setCurrentPage },
  } = useSignatureContext()
  const signerColor = activeSigner?.color ?? SIGNER_COLOR_CODES[0]
  const contrastColor = useMemo(() => getContrastText(signerColor), [signerColor])
  const classes = useStyles({ signerColor, contrastColor })
  const activeSignerIndex = signers.findIndex((signer) => signer.email === activeSigner.email)

  const name = useMemo(() => {
    switch (activeSigner.multiSelectType) {
      case MultiSelectTypes.User:
      case MultiSelectTypes.Client:
        if (!activeSigner.firstName && !activeSigner.lastName) { return activeSigner.email }
        return `${activeSigner.firstName ?? ''} ${activeSigner.lastName ?? ''}`
      default:
        return activeSigner.name ?? ''
    }
  }, [activeSigner])

  useEffect(() => {
    if (isNaN(currentItemIndex) || !items?.length) { return }
    const currentSignature = items[currentItemIndex]
    if (currentSignature.signerEmail !== activeSigner.email) {
      const updatedActiveSigner = signers.find(signer => signer.email === currentSignature.signerEmail)
      dispatch(setActiveSigner({ signer: updatedActiveSigner }))
    }
  }, [currentItemIndex])
  const handleItemClick = (index: number, signature: Signature): void => {
    setCurrentItemIndexWithScroll(index, true)
    setCurrentPage(signature.position.page)
  }
  const handleItemDelete = (index: number): void => {
    removeItem(index)
  }

  const handleAddItem = (type: 'signature' | 'date' | 'text') => {
    const { email, color } = activeSigner
    if (!placingMode) {
      setPlacingMode(true)
    }

    addItem({
      type,
      responsibleUserName: name,
      page: currentPage,
      signerEmail: email,
      color,
    })
  }

  const guideSteps = useMemo(() => {
    return steps.map((step) => (
      <div key={`step-${step.name}-${name}`} className={classes.step}>
        <div className={classes.stepTitle}>{step.label}</div>
        <div>{step.description}</div>
      </div>
    ))
  }, [name])

  const itemEls = useMemo(() => {
    return items
      .map((item, index) => {
        const active = currentItemIndex === index
        if (item.signerEmail !== activeSigner.email) { return null }

        return (
          <SignaturePlacementItem
            key={`signature-item-${index}`}
            index={index}
            item={item}
            active={active}
            onClick={handleItemClick}
            onDelete={handleItemDelete} />
        )
      })
  }, [items, activeSigner, currentItemIndex])

  const getTooltip = (type: string) => {
    return (
      <div className={classes.tooltipContent}>
        <div className={classes.tooltipTitle}>{lang.signature.placement.tooltip[type]?.title}</div>
        <div
          className={classes.tooltipDescription}
          dangerouslySetInnerHTML={{ __html: lang.signature.placement.tooltip[type]?.description }}>
        </div>
      </div>
    )
  }

  const signatureTooltip = useMemo(() => {
    return getTooltip(SIGNATURE_FIELD_TYPES.SIGNATURE)
  }, [])
  const dateTooltip = useMemo(() => {
    return getTooltip(SIGNATURE_FIELD_TYPES.DATE)
  }, [])
  const textTooltip = useMemo(() => {
    return getTooltip(SIGNATURE_FIELD_TYPES.TEXT)
  }, [])

  return (
    <div>
      <div>
        <div className={classes.title}>{lang.signature.placement.title}</div>
        <div className={classes.steps}>{guideSteps}</div>
      </div>
      <div className={classes.divider} />
      <div className={classes.signerInfo}>
        <div className={classes.title}>
          <div className={classes.colorPreview}></div>
          {name}
        </div>
        <div className={classes.signerCount}>{`${activeSignerIndex + 1}/${signers.length}`}</div>
      </div>
      <div className={classes.addArea}>
        <SignaturePlacementAdd
          type={SIGNATURE_FIELD_TYPES.SIGNATURE}
          icon={SignaturePenIcon}
          tooltipContent={signatureTooltip}
          label={lang.signature.placement.button.signature}
          onAdd={handleAddItem} />
      </div>
      <div className={classes.addArea}>
        <SignaturePlacementAdd
          type={SIGNATURE_FIELD_TYPES.TEXT}
          icon={TextIcon}
          tooltipContent={textTooltip}
          label={lang.signature.placement.button.text}
          onAdd={handleAddItem} />
        <SignaturePlacementAdd
          type={SIGNATURE_FIELD_TYPES.DATE}
          icon={CalendarIcon}
          tooltipContent={dateTooltip}
          label={lang.signature.placement.button.date}
          onAdd={handleAddItem} />
      </div>
      <div className={classes.divider} />
      <div className={classes.signatureItems}>
        <div className={classes.title}>{lang.signature.placement.subTitle}</div>
        {itemEls}
      </div>
    </div>
  )
}

export default SignaturePlacement
